import React from 'react';
import { normalisePolyline } from './geometry';

// The served administration value for this instrument.
const DEFAULT_WINDOW_MS = 6000;

// VIEW and PADDING belong WITH PolylineFigure and must move with it if it is
// ever extracted into a shared figures.jsx. Leaving them behind while the
// extracted copy defines its own padding lets the two drift, and two figures
// drawn at different paddings is the shared-transform bug in another costume.
const VIEW = 100;
const PADDING = 12;

// One figure, normalised against its OWN bounding box. Never share a transform
// with the other figure — the probe is rotated about the origin and its raw
// coordinates land far outside the reference's box, routinely negative.
//
// NO START-POINT MARKER. A marker on the first point was a total answer leak:
// every glyph began with an eastward step and the mirror reflects across the
// vertical axis, so the probe's first segment pointed up on every SAME trial and
// down on every MIRROR trial — 100% accurate at every angle, without ever
// looking at the reference. The backend closes this three ways (balanced start
// directions, a shared random base orientation, per-figure traversal direction);
// dropping the marker is the fourth and it is ours. An open polyline reads fine
// without one, so nothing here may draw a dot, a marker, or an arrowhead.
function PolylineFigure({ points, size, label }) {
  const pts = normalisePolyline(points, { size: VIEW, padding: PADDING });
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="img"
      aria-label={label}
      focusable="false"
    >
      {pts.length > 0 && (
        <polyline
          points={pts.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="none"
          stroke="var(--text-primary)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

// angleDeg is deliberately never read here. It is a difficulty label served so
// a later analysis can stratify by it; showing it tells the participant how hard
// the trial is, and reading it at all is a step towards reconstructing an answer
// the payload withholds.
function StimulusView({ stimulus, compact }) {
  const size = compact ? 46 : 170;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 36 }}>
      <PolylineFigure points={stimulus.reference} size={size} label="Reference figure" />
      <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)' }} />
      <PolylineFigure points={stimulus.probe} size={size} label="Probe figure" />
    </div>
  );
}

export const mentalRotationTask = {
  id: 'mental-rotation',
  name: 'Turned or Flipped',
  pillar: 'spatial',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'Two open figures appear side by side.',
    'Decide whether the right figure is the left one simply turned around...',
    '...or whether it has been flipped into a mirror image.',
    'The difficulty does not adjust — this one measures rather than trains.',
  ],
  // Negative LEFT, affirmative RIGHT — the battery-wide convention, so a
  // participant moving between instruments never has to re-learn which side
  // means "yes"; here the affirmative is 'same' (the figure is merely turned).
  // ResponseTargets renders this array left-to-right with no reordering, so the
  // keys and hint belong to the POSITION, not to the id: ArrowLeft/f must
  // always sit on the left button whatever it answers.
  targets: [
    { id: 'mirror', label: 'Mirrored', keys: ['ArrowLeft', 'f'], hint: 'F · ←' },
    { id: 'same', label: 'Turned', keys: ['ArrowRight', 'j'], hint: 'J · →' },
  ],
  // Listed in target order, so the card reads in the same sequence as the two
  // buttons underneath it.
  examples: [
    {
      stimulus: {
        reference: [[0, 0], [1, 0], [2, 0], [2, 1]],
        probe: [[0, 0], [-0.174, 0.985], [-0.347, 1.97], [0.638, 2.143]],
        angleDeg: 100,
      },
      response: 'Mirrored',
      note: 'the turn goes the other way',
    },
    {
      stimulus: {
        reference: [[0, 0], [1, 0], [2, 0], [2, 1]],
        probe: [[0, 0], [0.643, 0.766], [1.286, 1.532], [0.52, 2.175]],
        angleDeg: 50,
      },
      response: 'Turned',
      note: 'same path, rotated',
    },
  ],
  toAnswer: (targetId) => ({ isSame: targetId === 'same' }),
  // Fixed administration parameters, and deliberately blind to the per-trial
  // second argument: the angle set never adapts, so nothing about pacing may
  // vary with the trial. Number() is load-bearing — the runner's reaction-time
  // ceiling is windowMs + 250, and a window arriving as a string concatenates
  // instead of adding, lifting the ceiling into the millions.
  timing: (params) => ({
    fixationMs: 400,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: 500,
  }),
  // The answer is not in the payload, and deciding chirality client-side would
  // be reconstructing a withheld answer — so there is nothing to feed back,
  // on a practice trial or anywhere else.
  feedbackAvailable: false,
  practiceExpectation: () => null,
  StimulusView,
};
